import { useCallback, useEffect, useRef } from "react";

const DEFAULT_DELAY_MS = 80;

/**
 * Returns hover/focus handlers that call `prefetch(arg)` after a short
 * debounce. The debounce avoids spamming prefetches when the cursor merely
 * scrolls past a list of items.
 *
 * The returned handlers are stable across renders.
 */
export const usePrefetchHandlers = <Arg>(
  prefetch: (arg: Arg) => void,
  arg: Arg,
  delayMs: number = DEFAULT_DELAY_MS,
) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const argRef = useRef(arg);
  argRef.current = arg;

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const schedule = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      prefetch(argRef.current);
    }, delayMs);
  }, [prefetch, delayMs]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return {
    onPointerEnter: schedule,
    onFocus: schedule,
    onPointerLeave: cancel,
    onBlur: cancel,
  };
};
